import React from "react";
import { Link } from "react-router-dom";
import aboutWorkbench from "../../assets/content/about-workbench.webp";
import aboutCraftDetail from "../../assets/content/about-craft-detail.webp";
import "./aboutUsPage.scss";

const principles = [
  {
    title: "Clear choices",
    text: "Product types, materials, sizes, and prices are kept visible so customers understand what they are choosing before checkout.",
  },
  {
    title: "Material awareness",
    text: "Each piece can be presented with material-specific pricing and stock, so the catalog stays practical for real selling.",
  },
  {
    title: "Service after purchase",
    text: "Order history, checkout records, and customer support pages are part of the experience, not an afterthought.",
  },
];

const processSteps = [
  {
    title: "Browse by intent",
    text: "Customers can start from a category, a product type, or a gender collection and then narrow down to the right piece.",
  },
  {
    title: "Review the details",
    text: "Materials, sizes, product images, comments, and price are organized around the product page before it reaches the cart.",
  },
  {
    title: "Checkout with context",
    text: "Shipping address, tax calculation, payment method, and order confirmation are handled in a focused checkout flow.",
  },
];

const serviceItems = [
  "Thoughtful product information",
  "Secure checkout paths",
  "Order history for customers",
  "Admin-managed catalog and stock",
];

const AboutUs = () => {
  return (
    <main className="about-page">
      <section className="about-hero">
        <div className="about-hero__content">
          <p className="about-label">TrueKarat Jewelry</p>
          <h1>It started with one thought and grew into a company.</h1>
          <p>
            TrueKarat began with a simple belief: jewelry should feel personal,
            clear, and carefully made. What started as one idea at the workbench
            became a full catalog experience with products, materials, sizing,
            stock, checkout, and service built around the customer.
          </p>
        </div>

        <div className="about-hero__image" aria-label="Jewelry maker working at a bench">
          <img src={aboutWorkbench} alt="Jewelry tools and hands working at a jeweler bench" />
        </div>
      </section>

      <section className="about-section about-story">
        <div>
          <h2>From a single idea</h2>
          <p>
            The first version of TrueKarat was not about having the biggest
            catalog. It was about making the path from discovery to purchase
            honest and understandable: what the piece is, what material it uses,
            what size fits, what it costs, and what happens after checkout.
          </p>
        </div>
        <div>
          <h2>Into a complete store</h2>
          <p>
            That idea grew into a working commerce system: product types,
            material prices, stock, cart logic, checkout, paid orders, customer
            accounts, and an admin-managed catalog. The goal is a jewelry store
            that feels simple on the surface and reliable underneath.
          </p>
        </div>
      </section>

      <section className="about-section about-craft">
        <div className="about-craft__image">
          <img src={aboutCraftDetail} alt="Close-up of jeweler hands working on a ring" />
        </div>
        <div className="about-craft__copy">
          <p className="about-label">The craft</p>
          <h2>Details matter before the customer ever sees the product.</h2>
          <p>
            Every product page should carry the same care as the workbench:
            clear materials, readable prices, real stock, useful images, and a
            checkout path that does not make the customer guess.
          </p>
        </div>
      </section>

      <section className="about-section">
        <div className="about-section__header">
          <h2>Our principles</h2>
          <p>
            The strongest jewelry sites pair emotion with practical confidence.
            These are the product principles behind the current store.
          </p>
        </div>

        <div className="about-principles">
          {principles.map((item) => (
            <article className="about-principle" key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-section about-process">
        <div className="about-section__header">
          <h2>From browsing to order</h2>
          <p>
            The site is organized around the customer journey rather than a
            disconnected catalog.
          </p>
        </div>

        <div className="about-steps">
          {processSteps.map((step, index) => (
            <article className="about-step" key={step.title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-service">
        <div>
          <h2>Service is part of the product</h2>
          <p>
            Customers need more than a product grid. They need confidence before
            purchase and a clear place to return after purchase.
          </p>
        </div>

        <ul>
          {serviceItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <div className="about-actions">
          <Link to="/products">Shop products</Link>
          <Link to="/help">Visit help center</Link>
        </div>
      </section>
    </main>
  );
};

export default AboutUs;
